/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig
} from "axios";
import axios from "axios";

// ─── Constants ────────────────────────────────────────────────────────────────
const apiUrl = process.env.REACT_APP_API_URL as string;
const loginUrl = `${apiUrl}/auth/login`;
const refreshTokenUrl = `${apiUrl}/auth/refresh/token`;
const adminOrigin = process.env.REACT_APP_ADMIN ?? "*";

// ─── Types ────────────────────────────────────────────────────────────────────
interface RefreshTokenResponse {
  accessToken: string;
  accessTokenExpiry: string;
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface QueuedRequest {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

// ─── Token helpers ────────────────────────────────────────────────────────────
const TokenService = {
  getAccessToken: (): string | null => sessionStorage.getItem("token"),
  getRefreshToken: (): string | null => sessionStorage.getItem("refreshToken"),
  setTokens: (accessToken: string, refreshToken?: string): void => {
    sessionStorage.setItem("token", accessToken);
    if (refreshToken) sessionStorage.setItem("refreshToken", refreshToken);
  },
  clearTokens: (): void => {
    localStorage.clear();
    sessionStorage.clear();
  }
} as const;

// ─── Interceptor state ────────────────────────────────────────────────────────
let isRefreshing = false; // true while a refresh call is in-flight
let isLoggingOut = false; // true once logout has been triggered
let failedQueue: QueuedRequest[] = [];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token as string);
  });
  failedQueue = [];
};

/**
 * Clears tokens, notifies the parent frame, and flips the flag so no further
 * 401 handling (for queued or replayed requests) triggers a second logout.
 */
const logout = (): void => {
  if (isLoggingOut) return; // ✅ already logging out — do nothing
  isLoggingOut = true;

  TokenService.clearTokens();
  window.top?.postMessage({ type: "logout" }, adminOrigin);
};

// ─── Axios instance ────────────────────────────────────────────────────────────
const http: AxiosInstance = axios.create({ baseURL: apiUrl });

// ─── Request interceptor ───────────────────────────────────────────────────────
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = TokenService.getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error: unknown) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────
http.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,

  async (error: unknown): Promise<AxiosResponse> => {
    if (!axios.isAxiosError(error)) return Promise.reject(error);

    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;

    // ── Not a 401 or no config → pass through ─────────────────────────────────
    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // ── Already logging out → silently reject, no further action ──────────────
    // Covers every queued request that gets rejected after logout fires
    if (isLoggingOut) {
      return Promise.reject(error);
    }

    // ── 401 on login → pass through ───────────────────────────────────────────
    if (originalRequest.url === loginUrl) {
      return Promise.reject(error);
    }

    // ── 401 on the refresh endpoint → discard queue + logout ──────────────────
    if (originalRequest.url === refreshTokenUrl) {
      processQueue(error, null);
      logout();
      return Promise.reject(error);
    }

    // ── Already retried → pass through ────────────────────────────────────────
    // _retry is ONLY set on the request that owns the refresh,
    // never on queued requests, so they can be replayed correctly
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    // ── No tokens → logout immediately ────────────────────────────────────────
    const accessToken = TokenService.getAccessToken();
    const refreshToken = TokenService.getRefreshToken();

    if (!accessToken || !refreshToken) {
      logout();
      return Promise.reject(error);
    }

    // ── Refresh already in-flight → queue this request ────────────────────────
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken: string) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return http(originalRequest);
        })
        .catch((err: unknown) => Promise.reject(err));
    }

    // ── First 401 → own the refresh ───────────────────────────────────────────
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const { data, status: refreshStatus } =
        await http.post<RefreshTokenResponse>(refreshTokenUrl, {
          accessToken,
          refreshToken
        });

      if (refreshStatus !== 200 || !data?.accessToken) {
        throw new Error("Invalid refresh response");
      }

      const { accessToken: newAccess } = data;

      TokenService.setTokens(newAccess);
      http.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
      window.top?.postMessage(
        { type: "updateAccessToken", token: newAccess },
        adminOrigin
      );
      processQueue(null, newAccess); // unblock queued requests

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return http(originalRequest); // replay the original
    } catch (refreshError: unknown) {
      processQueue(refreshError, null); // reject queued requests
      logout(); // single logout call
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// ─── Typed export ──────────────────────────────────────────────────────────────
const apiClient = {
  get: http.get.bind(http) as AxiosInstance["get"],
  post: http.post.bind(http) as AxiosInstance["post"],
  put: http.put.bind(http) as AxiosInstance["put"],
  patch: http.patch.bind(http) as AxiosInstance["patch"],
  delete: http.delete.bind(http) as AxiosInstance["delete"],
  http
} as const;

export type ApiClient = typeof apiClient;
export default apiClient;
